import { Button } from 'components/core';
import { useProceedToIteroCheckout } from 'reactQuery/mutations';
import { Styled } from '.';

import { Dispatch, SetStateAction } from 'react';
import { IteroProposalClient } from 'types';
import { shouldDisplaySavings } from 'components/OrderSummary';
import { ITeroCheckoutStates } from 'components/ITeroCheckout';
import { useI18n } from 'contexts/i18n';

export const ITeroProposalSummary = ({
  orderStep,
  currentProposal,
  setProceedToCheckout,
  displayTax,
  displayTotalAfterTax,
  estimatedMonthlyPayment,
}: {
  orderStep: 'checkout' | ITeroCheckoutStates;
  currentProposal: IteroProposalClient;
  setProceedToCheckout: Dispatch<SetStateAction<boolean>>;
  displayTax?: string;
  displayTotalAfterTax?: string;
  estimatedMonthlyPayment?: string;
}) => {
  const proceedToIteroCheckoutMutation = useProceedToIteroCheckout(
    currentProposal.proposalId,
    setProceedToCheckout
  );
  const i18n = useI18n();

  const redirectToContract = () => {
    proceedToIteroCheckoutMutation.mutate();
  };

  return (
    <Styled.SummaryContainer>
      <Styled.Summary>
        <Styled.SummaryItem>
          <Styled.SummaryTitle>{i18n['Summary']}</Styled.SummaryTitle>
        </Styled.SummaryItem>

        <Styled.SummaryItem>
          <div>{i18n['Proposal Number']}:</div>
          <div>
            {orderStep === 'checkout' ? (
              <div>{currentProposal.proposalName}</div>
            ) : (
              <Styled.ProposalLinkWrapper
                href={`/iteroscanners/proposals/${currentProposal.proposalName}?showproposal=1`}
              >
                {currentProposal.proposalName}
              </Styled.ProposalLinkWrapper>
            )}
          </div>
        </Styled.SummaryItem>

        <Styled.SummaryItem>
          <div>{i18n['Total Net Price']}:</div>
          <div>{currentProposal.displayTotal}</div>
        </Styled.SummaryItem>
        {shouldDisplaySavings(currentProposal.displayAdjustmentTotal) && (
          <Styled.SummaryItem>
            <div>{i18n['Total Promotion/Discount Amount']}:</div>
            <div>
              <Styled.DiscountAmount>
                (-{currentProposal.displayAdjustmentTotal})
              </Styled.DiscountAmount>
            </div>
          </Styled.SummaryItem>
        )}

        {orderStep !== 'checkout' && (
          <Styled.SummaryItem>
            <div>{i18n['Estimated Tax']}:</div>
            {displayTax && orderStep !== 'address' ? (
              <div>
                <div>{displayTax}</div>
              </div>
            ) : (
              <div>{i18n['Calculated at next step']}</div>
            )}
          </Styled.SummaryItem>
        )}

        <Styled.Separator />

        <Styled.SummaryItem>
          <Styled.TotalPrice>
            {i18n['Total Purchase Price']}:
            <Styled.TaxNote>
              {displayTotalAfterTax && orderStep !== 'address'
                ? `(${i18n['Inclusive of taxes']})`
                : `(${i18n['Exclusive of taxes']})`}
            </Styled.TaxNote>
          </Styled.TotalPrice>
          <Styled.TotalPrice>
            {displayTotalAfterTax && orderStep !== 'address'
              ? displayTotalAfterTax
              : currentProposal.displayTotalAfterAdjustments}
          </Styled.TotalPrice>
        </Styled.SummaryItem>

        {orderStep !== 'checkout' &&
          currentProposal.scannerSubFundingSource === 'EPT' && (
            <Styled.SummaryItem>
              <Styled.SummaryItemLabel>
                {i18n['Estimated Monthly Installment'] ??
                  'Estimated Monthly Installment'}
                :
              </Styled.SummaryItemLabel>
              <Styled.SummaryItemValue>
                {displayTax && orderStep !== 'address' ? (
                  <div>
                    <div>{estimatedMonthlyPayment}</div>
                  </div>
                ) : (
                  <div>{i18n['Calculated at next step']}</div>
                )}
              </Styled.SummaryItemValue>
            </Styled.SummaryItem>
          )}

        {displayTax && orderStep !== 'checkout' && orderStep !== 'address' && (
          <Styled.TaxInfoWrapper>
            {i18n['Final sales tax will be indicated on your invoice at the time of shipment.']}
          </Styled.TaxInfoWrapper>
        )}

        {orderStep === 'checkout' && (
          <Styled.ButtonWrapper>
            <Button
              size="sm"
              onClick={() => redirectToContract()}
              width="100%"
              loading={proceedToIteroCheckoutMutation.isLoading}
            >
              {i18n['Checkout']}
            </Button>
          </Styled.ButtonWrapper>
        )}
      </Styled.Summary>
    </Styled.SummaryContainer>
  );
};
